import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { WorkflowCaseStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { listWorkflowCases } from '@ebalance/proto/bffuser/v1/workflow_case_service-WorkflowCaseService_connectquery'
import {
  ListWorkflowCasesRequestSchema,
  type ListWorkflowCasesResponse,
} from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import type { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { WORKFLOW_CASE_REFETCH_INTERVAL_MS } from '~/hooks/workflowCase/refetchInterval'
import { parseIsoDate, toProtoDate } from '~/models/dateTime'
import { activeBigStepName } from '~/pages/WorkflowCases/models/activeBigStep'
import {
  type WorkflowCaseRow,
  workflowCaseCreatedViaOf,
} from '~/pages/WorkflowCases/models/workflowCase'

export type WorkflowCasesFilter = {
  section?: WorkflowSection
  /**
   * spec AC-05 (workflow-delete-transition design §4): segment + checkbox 2 個から
   * 導出した repeated statuses。空 = 全 status (List RPC 側の契約)。
   */
  statuses?: WorkflowCaseStatus[]
  /** spec AC-001/AC-06、workflow-fix-list AC-04/AC-06: エリアフィルタ (複数選択、chip 式 MultiSelect) */
  areaCodes?: AreaCode[]
  /** 対象期間 開始日 (YYYY-MM-DD) */
  targetDateFrom?: string
  /** 対象期間 終了日 (YYYY-MM-DD) */
  targetDateTo?: string
  /**
   * workflow-list-action-columns AC-07/AC-08、spec §機能詳細のフィルタ
   * (workflow.md §415): 「あなたが対応すべきもののみ」 (server-side
   * filter、既定 false)。viewer 自体は request に載せず BFF が eauth
   * セッションから注入する。
   */
  onlyViewerActionRequired?: boolean
}

function convertResponseToRows(
  resp: ListWorkflowCasesResponse,
): WorkflowCaseRow[] {
  return resp.cases.map((c) => {
    const totalSubSteps = c.bigSteps.reduce(
      (sum, bs) => sum + bs.subSteps.length,
      0,
    )
    const doneSubSteps = c.bigSteps.reduce(
      (sum, bs) => sum + bs.subSteps.filter((ss) => ss.isCompleted).length,
      0,
    )
    return {
      id: c.id,
      workflowId: c.workflowId,
      workflowTemplateId: c.workflowTemplateId,
      workflowTemplateName: c.workflowTemplateName,
      targetDate: c.targetDate,
      tags: c.tags,
      section: c.section,
      areaCode: c.areaCode,
      bgCode: c.bgCode,
      bgName: c.bgName,
      repBusinessName: c.repBusinessName,
      status: c.status,
      progress: { done: doneSubSteps, total: totalSubSteps },
      activeBigStepName: activeBigStepName(c.bigSteps, c.status),
      completedAt: c.completedAt,
      completedBy: c.completedBy,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      deletedAt: c.deletedAt,
      deletedBy: c.deletedBy,
      responderMemberIds: c.responderMemberIds,
      viewerAction: c.viewerAction,
      createdVia: workflowCaseCreatedViaOf(c.autoCreation),
    }
  })
}

export function useListWorkflowCases(
  filter?: WorkflowCasesFilter,
): UseQueryResult<ListWorkflowCasesResponse, ConnectError> & {
  rows: WorkflowCaseRow[]
} {
  // tag_labels フィルタは BFF request field としては存在する (backend の
  // list usecase まで実装済み) が、bgCode フィルタと同様 client-side filter
  // で実現する (`WorkflowCases/index.tsx` の filteredRows)。理由も同じ:
  // 選択肢一覧を取得済み rows から導出するため、サーバー往復を追加しない。
  const parsedTargetDateFrom =
    filter?.targetDateFrom !== undefined && filter.targetDateFrom !== ''
      ? parseIsoDate(filter.targetDateFrom)
      : undefined
  const targetDateFrom =
    parsedTargetDateFrom !== undefined
      ? toProtoDate(parsedTargetDateFrom)
      : undefined
  const parsedTargetDateTo =
    filter?.targetDateTo !== undefined && filter.targetDateTo !== ''
      ? parseIsoDate(filter.targetDateTo)
      : undefined
  const targetDateTo =
    parsedTargetDateTo !== undefined
      ? toProtoDate(parsedTargetDateTo)
      : undefined
  const result = useQuery(
    listWorkflowCases,
    create(ListWorkflowCasesRequestSchema, {
      section: filter?.section,
      statuses: filter?.statuses ?? [],
      areaCodes: filter?.areaCodes ?? [],
      onlyViewerActionRequired: filter?.onlyViewerActionRequired ?? false,
      targetDateFrom,
      targetDateTo,
    }),
    // AC-WFW-039 (Design Note `tmp/designing-feature/workflow-auto-update.md`
    // D4): 一覧表示中は WORKFLOW_CASE_REFETCH_INTERVAL_MS (30 秒) 間隔で自動
    // 再取得し、「あなたの対応」/「対応者」列 (AC-WFW-001) を再計算する。 本 hook
    // は `WorkflowCases/index.tsx` のみが利用するため、 直付けしても他画面へ
    // polling が波及しない (grep 確認済み)。
    {
      refetchInterval: WORKFLOW_CASE_REFETCH_INTERVAL_MS,
      // 背景 poll (refetchInterval) の失敗で一覧をフィルタ / ソート状態ごと
      // 全画面エラー (error boundary) に差し替えない。 data 未取得の間 (= 初回
      // ロード) だけ従来どおり throw して fail-closed を保ち、 表示中の背景再取得
      // 失敗は無視して直前の表示を維持する (詳細側 `useGetWorkflowCase` の
      // throwOnError: false と整合。 app 既定は throwOnError: true + 一過性エラー
      // のみ有限 retry = `providers/ApiClientProvider.tsx`)。
      throwOnError: (_error, query) => query.state.data === undefined,
    },
  )

  const rows =
    result.data !== undefined ? convertResponseToRows(result.data) : []

  return { ...result, rows }
}
